import {
    getDateTimeAsMoment,
    getFormattedDateTime,
} from '@eventbrite/datetime';
import { Trending as TrendingSvg } from '@eventbrite/eds-iconography';
import { gettext } from '@eventbrite/i18n';
import get from 'lodash/get';
import has from 'lodash/has';
import reduce from 'lodash/reduce';
import take from 'lodash/take';
import React from 'react';
import { formatUrl } from 'url-lib';
import {
    CATEGORY_LONG_NAME_ICON_MAP,
    EVENTBRITE_CATEGORY,
    EVENTBRITE_CATEGORY_MAPPING,
    EVENTBRITE_FORMAT_MAPPING,
    EVENTS_TYPE,
    EVENT_TYPE,
    FOOD_AND_DRINK_EVENTBRITE_CATEGORY,
    MUSIC_EVENTBRITE_CATEGORY,
    SUGGESTION_TYPE_POPULAR,
} from '../../../constants';
import { EVENT_AUTOCOMPLETE_AFFILIATE } from '../../../constants/constants';
import {
    CategoriesSuggestions,
    EventSuggestions,
    QuerySuggestions,
    Suggestion,
} from '../../../types/index';
import { getLocationInfo } from '../../../utils/venue';
import { getTransformedContent } from './transformation';

const _parseEventsSuggestions = (suggestions: EventSuggestions) => {
    let eventSuggestions = suggestions.map(
        (
            {
                id,
                name,
                startDate,
                startTime,
                timezone,
                primaryVenue,
                url,
                ticketsBy,
                image,
            }: Suggestion,
            index: number,
        ) => ({
            ticketsBy,
            value: `${EVENT_TYPE}/${id}`,
            content: name,
            imageUrl: image ? image?.url : '',
            imageAlt: name,
            secondaryContent: getFormattedDateTime(
                getDateTimeAsMoment(startDate, startTime),
                false,
                timezone,
            ),
            tertiaryContent: getLocationInfo(
                get(primaryVenue, 'name', ''),
                get(primaryVenue, 'address.localizedAreaDisplay', ''),
            ),
            header: index === 0 ? gettext('Events') : null,
            showDivider: suggestions.length === index + 1 || index === 4,
            dividerVertSpacing: 2,
            url: formatUrl(url, { aff: EVENT_AUTOCOMPLETE_AFFILIATE }),
            isSquareImage: true,
        }),
    );

    eventSuggestions = take(eventSuggestions, 5);

    return eventSuggestions;
};

const _parseCategoriesSuggestions = (suggestions: CategoriesSuggestions) => {
    let categoriesSuggestions = suggestions.map(
        (
            { tag, localized: { displayName: name } }: Suggestion,
            index: number,
        ) => ({
            value: tag,
            content:
                EVENTBRITE_CATEGORY_MAPPING[tag || ''] ||
                EVENTBRITE_FORMAT_MAPPING[tag || ''] ||
                name,
            iconType: CATEGORY_LONG_NAME_ICON_MAP[tag || ''],
            header: index === 0 ? gettext('Categories') : null,
            showDivider: suggestions.length === index + 1 || index === 4,
            dividerVertSpacing: 2,
        }),
    );

    categoriesSuggestions = take(categoriesSuggestions, 5);

    return categoriesSuggestions;
};

const _parseQuerySuggestions = (suggestions: QuerySuggestions) => {
    let querySuggestions = suggestions.map(
        (completion: string, index: number) => ({
            value: completion,
            content: completion,
            iconType: <TrendingSvg />,
            header: index === 0 ? gettext('Popular') : null,
            showDivider: suggestions.length === index + 1 || index === 4,
            dividerVertSpacing: 2,
            suggestionType: SUGGESTION_TYPE_POPULAR,
        }),
    );

    querySuggestions = take(querySuggestions, 5);

    return querySuggestions;
};

const PARSE_MAP: { [key: string]: Function } = {
    [EVENTS_TYPE]: _parseEventsSuggestions,
    categories: _parseCategoriesSuggestions,
    query: _parseQuerySuggestions,
    event: _parseEventsSuggestions,
};
//TODO: Consolidate this with utility in utils/suggestions
export const parseSuggestions = (response = {}) => {
    const { result } = getTransformedContent(
        'aKeyThatDoesNotMatterInThisContext',
        {
            response,
        },
    );
    const parsedResponse = reduce(
        result.response,
        (memo, value, key) => {
            if (key in PARSE_MAP) {
                const data = PARSE_MAP[key](value);

                // if we're parsing events, we need the unparsed version as well
                if (key === EVENTS_TYPE) {
                    const rawEvents = reduce(
                        value,
                        (acc, val) => ({
                            ...acc,
                            [val.id]: val,
                        }),
                        {},
                    );

                    return {
                        ...memo,
                        rawEvents,
                        [key]: data,
                    };
                }

                return {
                    ...memo,
                    [key]: data,
                };
            }

            return memo;
        },
        {},
    );

    return {
        ...parsedResponse,
    };
};
//TODO Consolidate this with utility in search app utils
export const transformSearchIntoSuggestions = (response = {}) => {
    const { result } = getTransformedContent(
        'aKeyThatDoesNotMatterInThisContext',
        {
            response,
        },
    );

    let events = [];

    let categories = [];

    let musicCategory;

    let foodAndDrinkCategory;

    if (has(result, 'response.events.results')) {
        events = result.response.events.results;

        if (has(result, 'response.events.aggs.tags.buckets')) {
            const tags = result.response.events.aggs.tags.buckets;

            categories = reduce(
                tags,
                (
                    memo: any,
                    tag: { inflated: { prefix?: string; tag?: string } },
                ) => {
                    const inflated = tag.inflated;

                    if (inflated && inflated.prefix === EVENTBRITE_CATEGORY) {
                        if (inflated.tag === MUSIC_EVENTBRITE_CATEGORY) {
                            musicCategory = inflated;
                        } else if (
                            inflated.tag === FOOD_AND_DRINK_EVENTBRITE_CATEGORY
                        ) {
                            foodAndDrinkCategory = inflated;
                        } else {
                            return [...memo, inflated];
                        }
                    }

                    return memo;
                },
                [],
            );
        }
    }

    if (foodAndDrinkCategory) {
        categories = [foodAndDrinkCategory, ...categories];
    }

    if (musicCategory) {
        categories = [musicCategory, ...categories];
    }

    return {
        events,
        categories,
    };
};
